import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  CircularProgress,
  Box,
} from '@mui/material';
import SeriesImageSelector from './SeriesImageSelector';
import Button from '../UIKit/Button';
import TextField from '../UIKit/TextField';
import Select from '../UIKit/Select';
import Switch from '../UIKit/Switch';
import { useTranslation } from 'react-i18next';

const BadgeSeriesDialog = ({
  open,
  onClose,
  mode,
  seriesFormData,
  formErrors,
  submitLoading,
  previewSeriesImage,
  setPreviewSeriesImage,
  imageUploadTab,
  handleImageTabChange,
  fileInputRef,
  handleChooseFile,
  handleSeriesFormChange,
  handleBonusRewardChange,
  handleSave,
  badges,
}) => {
  const { t } = useTranslation(['badges', 'common']);
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle>
        {mode === 'create'
          ? t('badges:badgeSeriesDialog.createTitle')
          : t('badges:badgeSeriesDialog.editTitle')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              name="series_name"
              label={t('badges:badgeSeriesDialog.seriesName')}
              fullWidth
              value={seriesFormData.name}
              onChange={(e) => handleSeriesFormChange('name', e.target.value)}
              error={!!formErrors.series_name}
              helperText={formErrors.series_name}
              required
            />

            <Box sx={{ mt: 2 }}>
              <TextField
                name="series_description"
                label={t('badges:badgeSeriesDialog.seriesDescription')}
                fullWidth
                multiline
                rows={2}
                value={seriesFormData.description}
                onChange={(e) =>
                  handleSeriesFormChange('description', e.target.value)
                }
                error={!!formErrors.series_description}
                helperText={formErrors.series_description}
                required
              />
            </Box>

            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth>
                <Select
                  multiple
                  value={seriesFormData.badges}
                  onChange={(e) =>
                    handleSeriesFormChange('badges', e.target.value)
                  }
                  label={t('badges:badgeSeriesDialog.selectBadges')}
                  error={!!formErrors.series_badges}
                  renderValue={(selected) => {
                    return selected
                      .map((badge) => {
                        const badgeData = badges.find((b) => b._id === badge);
                        return badgeData ? badgeData.name : badge.name;
                      })
                      .join(', ');
                  }}
                >
                  {badges.map((badge) => (
                    <MenuItem key={badge._id} value={badge._id}>
                      {badge.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText
                  sx={{ ml: 0 }}
                  error={!!formErrors.series_badges}
                >
                  {formErrors.series_badges ||
                    t('badges:badgeSeriesDialog.selectBadgesHelper')}
                </FormHelperText>
              </FormControl>
            </Box>

            <Box sx={{ mt: 2 }}>
              <TextField
                label={t('badges:badgeSeriesDialog.requiredBadgesCount')}
                type="number"
                fullWidth
                value={seriesFormData.requiredBadgesCount}
                onChange={(e) =>
                  handleSeriesFormChange(
                    'requiredBadgesCount',
                    parseInt(e.target.value)
                  )
                }
                InputProps={{ inputProps: { min: 1 } }}
                error={!!formErrors.series_requiredBadgesCount}
                helperText={
                  formErrors.series_requiredBadgesCount ||
                  t('badges:badgeSeriesDialog.requiredBadgesCountHelper')
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <SeriesImageSelector
              previewSeriesImage={previewSeriesImage}
              setPreviewSeriesImage={setPreviewSeriesImage}
              seriesFormData={seriesFormData}
              setSeriesFormData={(seriesFormData) =>
                handleSeriesFormChange('imageFile', seriesFormData.imageFile)
              }
              formErrors={formErrors}
              setFormErrors={() => {}}
              imageUploadTab={imageUploadTab}
              handleImageTabChange={handleImageTabChange}
              fileInputRef={fileInputRef}
              handleChooseFile={handleChooseFile}
              handleSeriesFormChange={handleSeriesFormChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Select
                value={seriesFormData.bonusRewards?.experience || 0}
                onChange={(e) =>
                  handleBonusRewardChange(
                    'experience',
                    parseInt(e.target.value)
                  )
                }
                label={t('badges:badgeSeriesDialog.bonusExperience')}
              >
                <MenuItem value={0}>
                  {t('badges:badgeSeriesDialog.noExperience')}
                </MenuItem>
                <MenuItem value={50}>
                  50 {t('badges:badgeSeriesDialog.experience')}
                </MenuItem>
                <MenuItem value={100}>
                  100 {t('badges:badgeSeriesDialog.experience')}
                </MenuItem>
                <MenuItem value={200}>
                  200 {t('badges:badgeSeriesDialog.experience')}
                </MenuItem>
                <MenuItem value={300}>
                  300 {t('badges:badgeSeriesDialog.experience')}
                </MenuItem>
                <MenuItem value={400}>
                  400 {t('badges:badgeSeriesDialog.experience')}
                </MenuItem>
                <MenuItem value={500}>
                  500 {t('badges:badgeSeriesDialog.experience')}
                </MenuItem>
                <MenuItem value={750}>
                  750 {t('badges:badgeSeriesDialog.experience')}
                </MenuItem>
                <MenuItem value={1000}>
                  1000 {t('badges:badgeSeriesDialog.experience')}
                </MenuItem>
              </Select>
              <FormHelperText sx={{ ml: 0 }}>
                {t('badges:badgeSeriesDialog.bonusExperienceHelper')}
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Select
                value={seriesFormData.bonusRewards?.specialBadge || ''}
                onChange={(e) =>
                  handleBonusRewardChange('specialBadge', e.target.value)
                }
                label={t('badges:badgeSeriesDialog.specialBadge')}
              >
                <MenuItem value="">{t('badges:badgeSeriesDialog.noBadge')}</MenuItem>
                {badges.map((badge) => (
                  <MenuItem key={badge._id} value={badge._id}>
                    {badge.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText sx={{ ml: 0 }}>
                {t('badges:badgeSeriesDialog.specialBadgeHelper')}
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={seriesFormData.active}
                  onChange={(e) =>
                    handleSeriesFormChange('active', e.target.checked)
                  }
                  sx={{ mx: 1 }}
                />
              }
              label={t('badges:badgeSeriesDialog.activeSeries')}
            />
            <FormHelperText sx={{ ml: 0 }}>
              {t('badges:badgeSeriesDialog.activeSeriesHelper')}
            </FormHelperText>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={submitLoading}
        >
          {submitLoading ? (
            <CircularProgress size={24} />
          ) : mode === 'create' ? (
            t('common:actions.create')
          ) : (
            t('common:actions.save')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BadgeSeriesDialog;
